import { useMemo, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { updateFaithProfile } from "../../Services/faithProfile";
import {
  stepKey,
  visibleSteps,
  findPracticeSelector,
  findScheduleBuilder,
  buildPracticeLookup,
  buildLabelResolver,
  defaultAnswers,
  parseAnswersFromProfile,
  assembleProfileBody,
  resolveReviewRow,
  to24Hour,
  to12Hour,
} from "../../Utils/faithProfile";
import { ReactComponent as SmallScreenLeftArrow } from "../../Assets/icon/arrow-narrow-left-white.svg";
import { ReactComponent as LeftArrow } from "../../Assets/icon/arrow-narrow-left.svg";

import "../../Pages/TermsofUse/style.css";
import "./style.css";

// Phases: intro -> one screen per visible step -> review -> success.
function FaithWizard({ schema, initialProfile, faithTradition, onClose, onSaved }) {
  const [screenWidth] = useState(window.innerWidth);
  const [phase, setPhase] = useState("intro");
  const [stepId, setStepId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [{ answers: initialAnswers, existingEntries }] = useState(() =>
    initialProfile
      ? parseAnswersFromProfile(schema, initialProfile)
      : { answers: defaultAnswers(schema), existingEntries: [] },
  );
  const [answers, setAnswers] = useState(initialAnswers);

  const selector = useMemo(() => findPracticeSelector(schema), [schema]);
  const builder = useMemo(() => findScheduleBuilder(schema), [schema]);
  const practiceLookup = useMemo(() => buildPracticeLookup(schema), [schema]);
  const resolveLabel = useMemo(() => buildLabelResolver(schema), [schema]);

  const steps = useMemo(() => visibleSteps(schema, answers), [schema, answers]);
  const currentIndex = steps.findIndex((s) => s.id === stepId);
  const currentStep = currentIndex >= 0 ? steps[currentIndex] : null;

  const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  // ----- navigation -----
  const goNext = () => {
    setError("");
    if (phase === "intro") {
      if (steps.length) {
        setPhase("steps");
        setStepId(steps[0].id);
      } else {
        setPhase("review");
      }
      return;
    }
    if (phase === "steps") {
      // recompute visible steps from the latest answers
      const fresh = visibleSteps(schema, answers);
      const idx = fresh.findIndex((s) => s.id === stepId);
      if (idx >= 0 && idx + 1 < fresh.length) {
        setStepId(fresh[idx + 1].id);
      } else {
        setPhase("review");
      }
    }
  };

  const goBack = () => {
    setError("");
    if (phase === "intro") {
      onClose?.();
      return;
    }
    if (phase === "review") {
      if (steps.length) {
        setPhase("steps");
        setStepId(steps[steps.length - 1].id);
      } else {
        setPhase("intro");
      }
      return;
    }
    if (phase === "steps") {
      const idx = steps.findIndex((s) => s.id === stepId);
      if (idx > 0) setStepId(steps[idx - 1].id);
      else setPhase("intro");
    }
  };

  // ----- answer handlers -----
  const onSingleSelect = (step, value) => setAnswer(stepKey(step), value);

  const onToggleMulti = (step, value) => {
    const key = stepKey(step);
    const current = answers[key] || [];
    const max = step.max_select;
    let next;
    if (current.includes(value)) {
      next = current.filter((v) => v !== value);
    } else {
      if (max && current.length >= max) return;
      next = [...current, value];
    }
    setAnswers((prev) => {
      const updated = { ...prev, [key]: next };
      // Prune schedule times for any practice that was just removed.
      if (step.role === "practice_selector" && builder) {
        const bKey = stepKey(builder);
        const times = { ...(prev[bKey] || {}) };
        Object.keys(times).forEach((v) => {
          if (!next.includes(v)) delete times[v];
        });
        updated[bKey] = times;
      }
      return updated;
    });
  };

  const onToggleTime = (practiceValue, time) => {
    if (!builder) return;
    const bKey = stepKey(builder);
    setAnswers((prev) => {
      const times = { ...(prev[bKey] || {}) };
      const current = times[practiceValue] || [];
      times[practiceValue] = current.includes(time)
        ? current.filter((t) => t !== time)
        : [...current, time].sort();
      return { ...prev, [bKey]: times };
    });
  };

  const onAddCustomTime = (practiceValue, rawTime) => {
    if (!builder) return;
    const time = to24Hour(rawTime);
    if (!time) return;
    const bKey = stepKey(builder);
    setAnswers((prev) => {
      const times = { ...(prev[bKey] || {}) };
      const current = times[practiceValue] || [];
      if (current.includes(time)) return prev;
      times[practiceValue] = [...current, time].sort();
      return { ...prev, [bKey]: times };
    });
  };

  // ----- per-step validation -----
  const canContinue = (step) => {
    if (!step) return true;
    const key = stepKey(step);
    switch (step.type) {
      case "single_select":
        return !step.required || Boolean(answers[key]);
      case "multi_select":
        return (answers[key]?.length || 0) >= (step.min_select || 0);
      case "schedule_builder": {
        const selected = answers[stepKey(selector || {})] || [];
        const min = step.min_times_per_practice || 0;
        const times = answers[key] || {};
        return selected.every((v) => (times[v]?.length || 0) >= min);
      }
      default:
        return true;
    }
  };

  // ----- save -----
  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const body = assembleProfileBody(schema, answers, { faithTradition, existingEntries });
      // Snapshot the member's IANA timezone so prayer reminders fire locally
      // (server falls back to UTC otherwise). Retry without it on a 422.
      const timezone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
      try {
        await updateFaithProfile(timezone ? { ...body, timezone } : body);
      } catch (e) {
        if (timezone && e?.response?.status === 422) await updateFaithProfile(body);
        else throw e;
      }
      onSaved?.();
      setPhase("success");
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "We couldn't save your preferences. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  // ----- render -----
  const headerTitle = phase === "success" ? "" : "Faith & Spirituality";
  const previewBody = useMemo(
    () => assembleProfileBody(schema, answers, { faithTradition, existingEntries }),
    [schema, answers, faithTradition, existingEntries],
  );

  return (
    <div className="settings-container faith-wizard">
      <header className="termsofuse-header">
        <div className="termsofuse-gradient-background ">
          <div className="termsofuse-nav-container">
            <div className="termsofuse-logo-container " onClick={goBack}>
              {phase !== "success" &&
                (screenWidth < 480 ? (
                  <SmallScreenLeftArrow className="termsofuse-back-button" />
                ) : (
                  <LeftArrow className="termsofuse-back-button" />
                ))}
            </div>
            <h1 className="head-not-txt">{headerTitle}</h1>
            <div className="termsofuse-space"></div>
          </div>
        </div>
      </header>

      <div className="settings-hero-container">
        <div className="faith-wizard-content">
          {phase === "steps" && steps.length > 0 && (
            <div className="faith-wizard-progress">
              <div className="faith-wizard-progress-label">
                Step {currentIndex + 1} of {steps.length}
              </div>
              <div className="faith-wizard-progress-track">
                <div
                  className="faith-wizard-progress-fill"
                  style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {error && <div className="settings-error-banner">{error}</div>}

          {phase === "intro" && (
            <StepShell
              eyebrow={schema?.intro?.eyebrow}
              title={schema?.intro?.title || "Personalize your faith experience"}
            >
              {(schema?.intro?.body || []).map((p, i) => (
                <p className="faith-step-subtitle" key={i}>
                  {p}
                </p>
              ))}
              <div className="faith-wizard-actions">
                <button className="faith-btn faith-btn-primary" onClick={goNext}>
                  Get started
                </button>
              </div>
            </StepShell>
          )}

          {phase === "steps" && currentStep && (
            <StepShell title={currentStep.title} subtitle={currentStep.subtitle} hint={currentStep.hint}>
              <StepInput
                step={currentStep}
                answers={answers}
                practiceLookup={practiceLookup}
                builderKey={builder ? stepKey(builder) : null}
                onSingleSelect={onSingleSelect}
                onToggleMulti={onToggleMulti}
                onToggleTime={onToggleTime}
                onAddCustomTime={onAddCustomTime}
              />
              <div className="faith-wizard-actions">
                <button
                  className="faith-btn faith-btn-primary"
                  onClick={goNext}
                  disabled={!canContinue(currentStep)}
                >
                  {currentIndex + 1 === steps.length ? "Review" : "Continue"}
                </button>
              </div>
            </StepShell>
          )}

          {phase === "review" && (
            <StepShell title={schema?.review?.title || "Review your preferences"}>
              <div className="faith-summary">
                {(schema?.review?.rows || []).map((row, i) => {
                  const { label, lines } = resolveReviewRow(row, previewBody, resolveLabel);
                  return (
                    <div className="faith-summary-item" key={i}>
                      <div className="faith-summary-label">{label}</div>
                      {lines.map((line, j) => (
                        <div className="faith-summary-value" key={j}>
                          {line}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              <div className="faith-wizard-actions">
                <button className="faith-btn faith-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : schema?.review?.cta || "Save Preferences"}
                </button>
                <button className="faith-btn faith-btn-ghost" onClick={goBack} disabled={saving}>
                  Back
                </button>
              </div>
            </StepShell>
          )}

          {phase === "success" && (
            <StepShell
              title="You're all set"
              subtitle="Your faith preferences are saved. Aris will weave them into your coaching, and any reminders you chose are scheduled."
            >
              <div className="faith-wizard-success-icon">
                <Check size={40} />
              </div>
              <div className="faith-wizard-actions">
                <button className="faith-btn faith-btn-primary" onClick={() => onClose?.()}>
                  Done
                </button>
              </div>
            </StepShell>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step input renderer — dispatches by schema `type`. Unknown types render
// nothing (forward-compatible).
// ---------------------------------------------------------------------------
function StepInput({
  step,
  answers,
  practiceLookup,
  builderKey,
  onSingleSelect,
  onToggleMulti,
  onToggleTime,
  onAddCustomTime,
}) {
  const key = step.field || step.id;

  if (step.type === "single_select") {
    return (
      <div className="faith-choice-list">
        {(step.options || []).map((option) => {
          const selected = answers[key] === option.value;
          return (
            <button
              key={String(option.value)}
              type="button"
              className={`faith-choice ${selected ? "selected" : ""}`}
              onClick={() => onSingleSelect(step, option.value)}
            >
              <span className="faith-choice-body">
                <span className="faith-choice-label">{option.label}</span>
                {option.description && (
                  <span className="faith-choice-description">{option.description}</span>
                )}
              </span>
              <span className={`faith-radio ${selected ? "checked" : ""}`} />
            </button>
          );
        })}
      </div>
    );
  }

  if (step.type === "boolean") {
    const options = step.options?.length
      ? step.options
      : [{ value: true, label: "Yes" }, { value: false, label: "No" }];
    return (
      <div className="faith-choice-list faith-yesno">
        {options.map((option) => {
          const selected = answers[key] === option.value;
          return (
            <button
              key={String(option.value)}
              type="button"
              className={`faith-choice ${selected ? "selected" : ""}`}
              onClick={() => onSingleSelect(step, option.value)}
            >
              <span className="faith-choice-label">{option.label}</span>
              <span className={`faith-radio ${selected ? "checked" : ""}`} />
            </button>
          );
        })}
      </div>
    );
  }

  if (step.type === "multi_select") {
    const selected = answers[key] || [];
    const atMax = step.max_select && selected.length >= step.max_select;
    return (
      <div className="faith-choice-list">
        {(step.options || []).map((option) => {
          const isSel = selected.includes(option.value);
          return (
            <button
              key={String(option.value)}
              type="button"
              className={`faith-choice ${isSel ? "selected" : ""}`}
              disabled={!isSel && atMax}
              onClick={() => onToggleMulti(step, option.value)}
            >
              <span className="faith-choice-body">
                <span className="faith-choice-label">{option.label}</span>
                {option.description && (
                  <span className="faith-choice-description">{option.description}</span>
                )}
              </span>
              <span className={`faith-checkbox ${isSel ? "checked" : ""}`}>
                {isSel && <Check size={14} />}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  if (step.type === "schedule_builder") {
    const selectedPractices = answers[step.depends_on ? step.depends_on : "practices"]
      // depends_on names the practice-selector step id; fall back to scanning answers
      || [];
    const times = (builderKey && answers[builderKey]) || {};
    if (!selectedPractices.length) {
      return <div className="faith-wizard-empty">No practices selected. Go back to add some.</div>;
    }
    return (
      <>
        {selectedPractices.map((value) => (
          <RhythmPractice
            key={value}
            label={practiceLookup[value]?.label || value}
            presets={step.time_presets || []}
            allowCustom={step.allow_custom_time}
            times={times[value] || []}
            onToggleTime={(t) => onToggleTime(value, t)}
            onAddCustom={(t) => onAddCustomTime(value, t)}
          />
        ))}
      </>
    );
  }

  return null;
}

function RhythmPractice({ label, presets, allowCustom, times, onToggleTime, onAddCustom }) {
  const [customTime, setCustomTime] = useState("");
  const presetTimes = presets.map((p) => p.time_local);
  const customTimes = times.filter((t) => !presetTimes.includes(t));

  return (
    <div className="faith-rhythm">
      <div className="faith-rhythm-name">{label}</div>
      <div className="faith-rhythm-presets">
        {presets.map((preset) => {
          const active = times.includes(preset.time_local);
          return (
            <button
              key={preset.key || preset.time_local}
              type="button"
              className={`faith-time-chip ${active ? "active" : ""}`}
              onClick={() => onToggleTime(preset.time_local)}
            >
              <span className="faith-time-chip-label">{preset.label}</span>
              <span className="faith-time-chip-time">{to12Hour(preset.time_local)}</span>
            </button>
          );
        })}
      </div>
      {customTimes.length > 0 && (
        <div className="faith-rhythm-custom-list">
          {customTimes.map((t) => (
            <span key={t} className="faith-time-chip active faith-time-chip-custom">
              {to12Hour(t)}
              <button type="button" className="faith-time-remove" onClick={() => onToggleTime(t)}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      {allowCustom && (
        <div className="faith-rhythm-add">
          <input
            type="time"
            value={customTime}
            onChange={(e) => setCustomTime(e.target.value)}
            className="faith-time-input"
            aria-label={`Add another time for ${label}`}
          />
          <button
            type="button"
            className="faith-add-time-btn"
            disabled={!customTime}
            onClick={() => {
              onAddCustom(customTime);
              setCustomTime("");
            }}
          >
            <Plus size={14} /> Add another time
          </button>
        </div>
      )}
    </div>
  );
}

function StepShell({ eyebrow, title, subtitle, hint, children }) {
  return (
    <div className="faith-step">
      {eyebrow && <div className="faith-step-eyebrow">{eyebrow}</div>}
      <h2 className="faith-step-title">{title}</h2>
      {subtitle && <p className="faith-step-subtitle">{subtitle}</p>}
      {hint && <p className="faith-step-hint">{hint}</p>}
      {children}
    </div>
  );
}

export default FaithWizard;
